<template>
  <div>
    <!-- step indicator (mobile-only via CSS) -->
    <div
      class="sp-step-indicator"
      :aria-label="'שלב ' + signupStep + ' מתוך 2'"
    >
      <span
        class="sp-step-dot"
        :class="{ 'sp-step-dot--active': signupStep >= 1 }"
        aria-hidden="true"
      >1</span>
      <span class="sp-step-line" aria-hidden="true"></span>
      <span
        class="sp-step-dot"
        :class="{ 'sp-step-dot--active': signupStep >= 2 }"
        aria-hidden="true"
      >2</span>
    </div>

    <!-- Generic server error banner -->
    <div
      v-if="signupBanner"
      class="sp-banner sp-banner--error"
      role="alert"
      aria-live="assertive"
    >{{ signupBanner }}</div>

    <!-- ======== STEP 1 ======== -->
    <div v-show="signupStep === 1">
      <form @submit.prevent="advanceToStep2()">
        <ul class="frmUL">

          <!-- שם פרטי -->
          <li class="sp-field">
            <label class="sp-label" for="signup-first_name">
              שם פרטי
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-first_name"
                type="text"
                v-model="signup.first_name"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.first_name)"
                :aria-describedby="signupErrors.first_name ? 'err-first_name' : null"
                @blur="touchField('first_name')"
              />
            </span>
            <span
              v-if="signupErrors.first_name"
              id="err-first_name"
              class="sp-error"
              role="alert"
            >{{ signupErrors.first_name }}</span>
          </li>

          <!-- שם משפחה -->
          <li class="sp-field">
            <label class="sp-label" for="signup-last_name">
              שם משפחה
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-last_name"
                type="text"
                v-model="signup.last_name"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.last_name)"
                :aria-describedby="signupErrors.last_name ? 'err-last_name' : null"
                @blur="touchField('last_name')"
              />
            </span>
            <span
              v-if="signupErrors.last_name"
              id="err-last_name"
              class="sp-error"
              role="alert"
            >{{ signupErrors.last_name }}</span>
          </li>

          <!-- כתובת מייל -->
          <li class="sp-field">
            <label class="sp-label" for="signup-email">
              כתובת מייל
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-email"
                type="email"
                v-model="signup.email"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.email)"
                :aria-describedby="signupErrors.email ? 'err-email' : null"
                @blur="touchField('email')"
              />
            </span>
            <span
              v-if="signupErrors.email"
              id="err-email"
              class="sp-error"
              role="alert"
            >{{ signupErrors.email }}</span>
          </li>

          <!-- טלפון נייד -->
          <li class="sp-field">
            <label class="sp-label" for="signup-phone">
              טלפון נייד
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field">
              <input
                id="signup-phone"
                type="tel"
                v-model="signup.phone"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.phone)"
                :aria-describedby="signupErrors.phone ? 'err-phone' : null"
                @blur="touchField('phone')"
              />
            </span>
            <span
              v-if="signupErrors.phone"
              id="err-phone"
              class="sp-error"
              role="alert"
            >{{ signupErrors.phone }}</span>
          </li>

          <!-- סיסמה + eye-toggle -->
          <li class="sp-field">
            <label class="sp-label" for="signup-password">
              סיסמה
              <span class="sp-required" aria-hidden="true">*</span>
            </label>
            <span class="field sp-field__password-wrap">
              <input
                id="signup-password"
                :type="showPassword ? 'text' : 'password'"
                v-model="signup.password"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.password)"
                :aria-describedby="signupErrors.password ? 'err-password' : null"
                @blur="touchField('password')"
              />
              <button
                type="button"
                class="sp-eye-btn"
                :aria-label="showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </span>
            <span
              v-if="signupErrors.password"
              id="err-password"
              class="sp-error"
              role="alert"
            >{{ signupErrors.password }}</span>
          </li>

          <!-- תנאי שימוש -->
          <li class="sp-field sp-field--checkbox">
            <label class="sp-checkbox-label" for="signup-terms">
              <input
                id="signup-terms"
                type="checkbox"
                v-model="signup.accept_terms"
                aria-required="true"
                :aria-invalid="String(!!signupErrors.terms)"
                :aria-describedby="signupErrors.terms ? 'err-terms' : null"
                @change="touchField('terms')"
              />
              <span>
                אני מאשר/ת את
                <a href="/terms" target="_blank" rel="noopener noreferrer">תנאי השימוש</a>
                ומדיניות הפרטיות
              </span>
            </label>
            <span v-if="signupErrors.terms" id="err-terms" class="sp-error" role="alert">
              יש לאשר את תנאי השימוש כדי להמשיך
            </span>
          </li>

          <!-- עדכונים ומבצעים -->
          <li class="sp-field sp-field--checkbox">
            <label class="sp-checkbox-label" for="signup-adv">
              <input
                id="signup-adv"
                type="checkbox"
                v-model="signup.accept_adv"
              />
              <span>אני מעוניין/ת לקבל עדכונים ומבצעים במייל</span>
            </label>
          </li>

          <!-- Submit -->
          <li class="sbm_area sp-field">
            <UiButton
              type="submit"
              variant="primary"
              size="lg"
              :block="true"
              :disabled="!isStep1Valid || signupLoading || plansLoading"
              :loading="plansLoading"
            >
              המשך
            </UiButton>
            <span v-if="plansFetchError" class="sp-error" role="alert" style="margin-top:8px;display:block;">
              שגיאה בטעינת המסלולים. נסה/י שוב.
            </span>
          </li>
        </ul>
      </form>

      <div class="sp-switch-link">
        כבר יש לך חשבון?
        <button type="button" class="sp-text-btn" @click="$emit('switch-tab', 'login')">
          התחבר
        </button>
      </div>
    </div>
    <!-- end step 1 -->

    <!-- ======== STEP 2: plan picker (post-registration) ======== -->
    <div v-show="signupStep === 2">
      <div
        v-if="registered"
        class="sp-banner sp-banner--success"
        role="status"
        aria-live="polite"
      >
        ההרשמה התבצעה בהצלחה! בחר/י מסלול כדי להתחיל.
      </div>

      <!-- Subscription section heading -->
      <div class="sp-step2-header">
        <h2 class="sp-step2-title">בחר/י את המסלול המתאים לך</h2>
        <p class="sp-step2-subtitle">גישה מלאה לכל התכנים במחיר שמתאים לך</p>
      </div>

      <div v-if="plansFetchError" class="sp-banner sp-banner--error" role="alert">
        לא הצלחנו לטעון את המסלולים כרגע.
        <button type="button" class="sp-inline-retry" @click="retryPlansFetch">נסה/י שוב</button>
      </div>

      <div v-if="plansLoading" class="sp-loading-state">טוען מסלולים...</div>

      <ul class="sp-plans" role="radiogroup" aria-label="בחירת מסלול">

        <!-- Free plan -->
        <li>
          <label class="sp-plan-card sp-plan-card--free" :class="{ 'sp-plan-card--selected': selectedPlan === 'free' }">
            <input
              type="radio"
              name="signup_plan"
              value="free"
              v-model="selectedPlan"
              class="sp-plan-radio"
            />
            <span class="sp-plan-content">
              <span class="sp-plan-header">
                <span class="sp-plan-name">המשך חינם</span>
              </span>
              <span class="sp-plan-price-wrap">
                <span class="sp-plan-price-main">₪0</span>
              </span>
              <ul class="sp-plan-benefits">
                <li>צפייה בתכנים נבחרים</li>
                <li>שדרוג בכל עת</li>
              </ul>
            </span>
          </label>
        </li>

        <!-- Paid plans -->
        <li v-for="(plan, idx) in plans" :key="plan.id">
          <label class="sp-plan-card" :class="{ 'sp-plan-card--selected': selectedPlan === plan.id, 'sp-plan-card--popular': plans.length >= 3 && idx === 1 }">
            <input
              type="radio"
              name="signup_plan"
              :value="plan.id"
              v-model="selectedPlan"
              class="sp-plan-radio"
            />
            <span class="sp-plan-content">
              <span class="sp-plan-header">
                <span class="sp-plan-name">{{ plan.name || 'מסלול' }}</span>
                <span v-if="plan.free_days > 0 || plan.discount_months > 0" class="sp-plan-badges">
                  <span v-if="plan.free_days > 0" class="sp-badge sp-badge--trial">
                    ניסיון {{ plan.free_days }} ימים
                  </span>
                  <span v-if="plan.discount_months > 0" class="sp-badge sp-badge--discount">
                    מבצע {{ plan.discount_months }} חודשים
                  </span>
                </span>
              </span>
              <span class="sp-plan-price-wrap">
                <span class="sp-plan-price-main">
                  ₪{{ effectivePrice(plan) || 0 }}
                </span>
                <span v-if="planPeriodLabel(plan)" class="sp-plan-price-period">{{ planPeriodLabel(plan) }}</span>
                <span v-if="plan.discount_months > 0 && Number(plan.price) > Number(effectivePrice(plan))" class="sp-plan-price-original">
                  ₪{{ plan.price }}
                </span>
              </span>
              <p class="sp-plan-desc">
                {{ plan.description || 'צפה בכל התכנים ללא הגבלה בכל מכשיר ובכל זמן.' }}
              </p>
              <ul class="sp-plan-benefits">
                <li v-for="(benefit, idx) in planBenefits(plan)" :key="idx">{{ benefit }}</li>
              </ul>
            </span>
          </label>
        </li>

      </ul>

      <div v-if="!plansLoading && !plansFetchError && plans.length === 0" class="sp-empty-state">
        אין מסלולים בתשלום זמינים כרגע. אפשר להמשיך במסלול החינמי.
      </div>

      <UiButton
        type="button"
        variant="primary"
        size="lg"
        :block="true"
        :disabled="signupLoading"
        :loading="signupLoading"
        @click="submitSignup()"
      >
        {{ selectedPlan === 'free' ? 'סיום הרשמה' : 'המשך לתשלום' }}
      </UiButton>
    </div>
    <!-- end step 2 -->

    <!-- Payment Modal -->
    <div v-if="showPaymentIframe" class="sp-payment-modal">
      <!-- Overlay -->
      <div class="sp-payment-modal__overlay" @click="closePaymentModal"></div>
      
      <!-- Modal Panel -->
      <div class="sp-payment-modal__panel">
        <!-- Close button -->
        <button
          type="button"
          class="sp-payment-modal__close"
          @click="closePaymentModal"
          aria-label="סגור"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <!-- Payment content -->
        <div class="sp-payment-modal__content">
          <payment-tranzila
            :user_id="paymentUserId"
            :uid="registeredUid"
            :sum="paymentSum"
            :plan_id="paymentPlanId"
            @payment_done="onPaymentDone"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UiButton        from '../ui/Button.vue';
import PaymentTranzila from '../payment_tranzila.vue';

export default {
  name: 'SignupPanel',
  components: { UiButton, PaymentTranzila },

  props: {
    initialStep: { type: Number, default: 1 },
    initialPlanId: { type: [Number, String], default: 0 },
  },

  data() {
    return {
      signupStep: this.initialStep || 1,
      signup: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        accept_terms: false,
        accept_adv: false,
      },
      signupErrors: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        terms: '',
      },
      signupTouched: {},
      signupSubmitAttempted: false,
      signupLoading: false,
      plansLoading: false,
      plansFetchError: false,
      signupBanner: '',
      showPassword: false,
      selectedPlan: (this.initialPlanId && Number(this.initialPlanId) > 0) ? Number(this.initialPlanId) : 'free',
      plans: [],
      userToken: '',
      registeredUid: 0,
      registered: false,
      showPaymentIframe: false,
      paymentUserId: '',
      paymentSum: 0,
      paymentPlanId: 0,
    };
  },

  computed: {
    isStep1Valid() {
      return (
        this.signup.first_name.trim().length > 0 &&
        this.signup.last_name.trim().length > 0 &&
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.signup.email) &&
        this.signup.phone.replace(/\D/g, '').length >= 9 &&
        this.signup.password.length >= 6 &&
        this.signup.accept_terms
      );
    },
  },

  mounted() {
    // If launched straight to step 2, pre-load plans list
    if (this.signupStep === 2) {
      this.plansLoading = true;
      this.api_call({
        url: 'get_plans',
        onComplete: (resp) => {
          this.plansLoading = false;
          this.plans = this.extractPlansFromResponse(resp);
          if (this.plans.length === 0) {
            this.plans = this.fallbackPlansFromStore();
          }
          this.plansFetchError = this.plans.length === 0;
        },
      }).catch(() => {
        this.plansLoading = false;
        this.plans = this.fallbackPlansFromStore();
        this.plansFetchError = this.plans.length === 0;
      });
    }
  },

  methods: {
    extractPlansFromResponse(resp) {
      if (!resp) return [];
      const data = resp.data || {};
      const body = data.data || {};
      return this.normalizePlans(body.data || body.plans || body.items || body);
    },

    fallbackPlansFromStore() {
      const rows = this.$store && this.$store.state && this.$store.state.restrictions_plans;
      if (!Array.isArray(rows)) return [];
      return rows
        .filter((r) => r && Number(r.id) > 0)
        .map((r) => ({
          id: Number(r.id),
          name: r.name || 'מסלול',
          price: Number(r.price || 0),
          discount_price: Number(r.price || 0),
          discount_months: 0,
          free_days: 0,
        }));
    },

    touchField(field) {
      this.$set(this.signupTouched, field, true);
      this.validateField(field);
    },

    validateField(field) {
      if (!this.signupTouched[field] && !this.signupSubmitAttempted) return;
      const s = this.signup;
      switch (field) {
        case 'first_name':
          this.signupErrors.first_name = s.first_name.trim() ? '' : 'שדה חובה';
          break;
        case 'last_name':
          this.signupErrors.last_name = s.last_name.trim() ? '' : 'שדה חובה';
          break;
        case 'email':
          this.signupErrors.email = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s.email)
            ? ''
            : 'כתובת מייל לא תקינה';
          break;
        case 'phone':
          this.signupErrors.phone = s.phone.replace(/\D/g, '').length >= 9
            ? ''
            : 'מספר טלפון לא תקין';
          break;
        case 'password':
          this.signupErrors.password = s.password.length >= 6
            ? ''
            : 'הסיסמה חייבת להכיל לפחות 6 תווים';
          break;
        case 'terms':
          this.signupErrors.terms = s.accept_terms ? '' : 'יש לאשר את תנאי השימוש';
          break;
      }
    },

    validateAllStep1() {
      this.signupSubmitAttempted = true;
      ['first_name', 'last_name', 'email', 'phone', 'password', 'terms'].forEach((f) => {
        this.$set(this.signupTouched, f, true);
        this.validateField(f);
      });
      return (
        !this.signupErrors.first_name &&
        !this.signupErrors.last_name &&
        !this.signupErrors.email &&
        !this.signupErrors.phone &&
        !this.signupErrors.password &&
        !this.signupErrors.terms
      );
    },

    // Step 1 submit — register user first (no plan), then fetch plans, then advance
    advanceToStep2() {
      if (!this.validateAllStep1()) return;
      this.signupBanner = '';
      this.signupLoading = true;

      const finishWithError = (msg) => {
        this.signupLoading = false;
        this.plansLoading = false;
        if (msg) this.signupBanner = msg;
      };

      const fetchPlansAndAdvance = () => {
        this.plansLoading = true;
        this.plansFetchError = false;
        this.api_call({
          url: 'get_plans',
          onComplete: (resp) => {
            this.plansLoading = false;
            this.signupLoading = false;
            this.plans = this.extractPlansFromResponse(resp);
            if (this.plans.length === 0) {
              this.plans = this.fallbackPlansFromStore();
              this.plansFetchError = this.plans.length === 0;
              this.signupStep = 2;
              return;
            }
            this.signupStep = 2;
          },
        }).catch(() => {
          // Fallback for environments where get_plans is unavailable.
          this.api_call({
            url: 'subscription/plans',
            onComplete: (resp2) => {
              this.plansLoading = false;
              this.signupLoading = false;
              this.plans = this.extractPlansFromResponse(resp2);
              if (this.plans.length === 0) {
                this.plans = this.fallbackPlansFromStore();
              }
              this.plansFetchError = this.plans.length === 0;
              this.signupStep = 2;
            },
          }).catch(() => {
            this.plansLoading = false;
            this.signupLoading = false;
            this.plans = this.fallbackPlansFromStore();
            this.plansFetchError = this.plans.length === 0;
            this.signupStep = 2;
          });
        });
      };

      this.api_call({
        url: 'register_new_user',
        data: {
          first_name: this.signup.first_name,
          last_name: this.signup.last_name,
          email: this.signup.email,
          phone: this.signup.phone,
          password: this.signup.password,
          accept_terms: this.signup.accept_terms,
          accept_adv: this.signup.accept_adv,
          plan_id: 0,
          source: 'in_app',
          utm_source: localStorage.getItem('utm_source') || '',
          utm_medium: localStorage.getItem('utm_medium') || '',
          utm_campaign: localStorage.getItem('utm_campaign') || '',
        },
        onComplete: (resp) => {
          const d = resp && resp.data && resp.data.data;
          if (d && d.success) {
            this.userToken = d.user_token;
            this.registeredUid = Number(d.uid) || 0;
            this.registered = true;
            localStorage.setItem('user_token', d.user_token);
            fetchPlansAndAdvance();
            return;
          }
          this.signupLoading = false;
          const errors = (d && d.errors) || [];
          if (errors.indexOf('email_taken') !== -1) {
            this.$emit('email-taken', this.signup.email);
          } else if (errors.indexOf('phone_taken') !== -1) {
            this.signupErrors.phone = 'מספר זה כבר רשום.';
            this.$set(this.signupTouched, 'phone', true);
            this.$nextTick(() => {
              const el = document.getElementById('signup-phone');
              if (el) el.focus();
            });
          } else if (errors.length) {
            this.signupBanner = 'אירעה שגיאה בהרשמה. נסה/י שוב.';
          } else {
            this.signupBanner = 'אירעה שגיאה. נסה/י שוב.';
          }
        },
      }).catch(() => {
        finishWithError('אירעה שגיאה בחיבור לשרת. נסה/י שוב.');
      });
    },

    // Step 2 — user already registered. Choose plan = redirect.
    submitSignup() {
      if (!this.registered || !this.userToken) {
        this.signupBanner = 'יש להשלים את שלב ההרשמה תחילה.';
        this.signupStep = 1;
        return;
      }
      if (this.selectedPlan === 'free' || !this.selectedPlan) {
        document.location = '/';
        return;
      }
      // create_payment now runs inside <payment-tranzila> on mount; just hand it the plan.
      // Coerce to Number — a numeric-string id would otherwise yield plan_id=0 and silently
      // route the component into its card-only branch (api/payment_res, not payments/notify).
      const planId = Number(this.selectedPlan);
      const plan = this.plans.find(p => Number(p.id) === planId);
      this.paymentUserId = this.userToken;
      this.paymentSum = plan ? this.effectivePrice(plan) : 0;
      this.paymentPlanId = planId > 0 ? planId : 0;
      this.showPaymentIframe = true;
    },

    onPaymentDone() {
      this.showPaymentIframe = false;
      document.location = '/';
    },

    closePaymentModal() {
      // User closed/cancelled payment - stay on step 2, keep plan selected
      this.showPaymentIframe = false;
    },

    // Helper: effective monthly price (discount_price if discount active, else regular)
    effectivePrice(plan) {
      return Number(plan.discount_months) > 0 ? Number(plan.discount_price || plan.price || 0) : Number(plan.price || 0);
    },

    // Helper: period-aware billing label. Plans without a `period` (e.g. the
    // fallback/free plans) render no label rather than assuming monthly.
    planPeriodLabel(plan) {
      // A free plan has no billing cadence to show, regardless of what
      // `period` the DB row carries (ENUM defaults to 'monthly').
      const price = plan && (Number(plan.discount_months) > 0 ? plan.discount_price : plan.price);
      if (!plan || !plan.period || !(Number(price) > 0)) return '';
      if (plan.period === 'yearly') return 'לשנה';
      if (plan.period === 'monthly') return 'לחודש';
      return '';
    },

    // Helper: generate benefit bullets from plan data
    planBenefits(plan) {
      const benefits = [];
      if (plan.free_days > 0) {
        benefits.push(`ניסיון חינם ל-${plan.free_days} ימים`);
      }
      if (plan.discount_months > 0) {
        benefits.push(`מחיר מוזל ל-${plan.discount_months} חודשים ראשונים`);
      }
      benefits.push('גישה מלאה לכל התכנים');
      benefits.push('צפייה בכל מכשיר');
      return benefits;
    },

    normalizePlans(raw) {
      let list = [];
      if (!raw) return list;
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw.items)) list = raw.items;
      else if (Array.isArray(raw.plans)) list = raw.plans;
      else if (typeof raw === 'object') list = Object.values(raw);

      return list
        .filter((p) => p && Number(p.id || p.plan_id) > 0)
        .map((p) => {
          const basePrice = Number(p.price || p.monthly_price || p.amount || 0);
          const discountPrice = Number(p.discount_price || p.discounted_price || p.sale_price || basePrice || 0);
          return {
            id: Number(p.id || p.plan_id),
            name: p.name || p.title || p.plan_name || 'מסלול',
            price: Number.isFinite(basePrice) ? basePrice : 0,
            discount_price: Number.isFinite(discountPrice) ? discountPrice : 0,
            discount_months: Number(p.discount_months || p.discount_duration || p.sale_months || 0),
            free_days: Number(p.free_days || p.trial_days || p.free_trial_days || 0),
            period: p.period || '',
          };
        });
    },

    retryPlansFetch() {
      this.plansLoading = true;
      this.plansFetchError = false;
      this.api_call({
        url: 'get_plans',
        onComplete: (resp) => {
          this.plansLoading = false;
          this.plans = this.extractPlansFromResponse(resp);
          if (this.plans.length === 0) {
            this.plans = this.fallbackPlansFromStore();
          }
          this.plansFetchError = this.plans.length === 0;
        },
      }).catch(() => {
        this.plansLoading = false;
        this.plans = this.fallbackPlansFromStore();
        this.plansFetchError = this.plans.length === 0;
      });
    },
  },

  watch: {
    showPaymentIframe(isOpen) {
      // Prevent body scroll when modal is open
      if (isOpen) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }
    },
  },

  beforeDestroy() {
    // Cleanup: ensure modal-open class is removed when component is destroyed
    document.body.classList.remove('modal-open');
  },
};
</script>

